import React, { useState, useMemo, useEffect } from 'react';
import { Grid } from '@mui/material';
import AuthContainer from 'components/AuthContainer';
import TopSection from 'components/TopSection/TopSection';
import Paper from '@mui/material/Paper';
import type { CampaignType, CampaignRecipientType } from 'types/campaignTypes';
import { useAppSelector } from 'store/hooks';

import {
  useGetUsersQuery,
  useGetCampaignsQuery,
  useLazyGetUpcomingCampaignsQuery,
  useDeleteCampaignMutation,
} from 'api/services/dataEndpoints';

import StyledActivities from './MessageScheduler.style';

import Calendar from './components/Calendar/Calendar';
import UpcomingCampaigns from './components/UpcomingCampaigns/UpcomingCampaigns';
import CampaignForm from './components/CampaignForm/CampaignForm';

const MessageScheduler: React.FC = () => {
  const userId = useAppSelector((state) => state.userSlice.user.id);

  const { data: campaigns } = useGetCampaignsQuery();
  const { data: recipients } = useGetUsersQuery(null);
  const [
    triggerGetUpcomingCampaignsQuery,
    { data: upcomingCampaigns },
  ] = useLazyGetUpcomingCampaignsQuery();

  const [deleteCampaign] = useDeleteCampaignMutation();

  const [selectedCampaign, setSelectedCampaign] = useState<CampaignType | null>(null);

  useEffect(() => {
    if (userId) {
      triggerGetUpcomingCampaignsQuery(userId);
    }
  }, [triggerGetUpcomingCampaignsQuery, userId]);

  const recipientList = useMemo(() => {
    if (!recipients) return [];

    const result: CampaignRecipientType[] = [];
    recipients.forEach((recipient) => {
      result.push({
        id: recipient.id,
        category: 'user',
        group: null,
        name: recipient?.custom_data?.name || recipient.email,
        email: recipient.email,
        phone: recipient?.custom_data?.phone || '',
        count: 1,
      });
    });
    return result;
  }, [recipients]);

  const handleSelectCampaignForEdit = (campaign: CampaignType) => {
    setSelectedCampaign(campaign);
  };

  const handleResetCampaignForEdit = () => {
    setSelectedCampaign(null);
  };

  const handleDeleteCampaign = async (campaign: CampaignType) => {
    await deleteCampaign({
      userId,
      campaignId: campaign.id,
    });
  };

  return (
    <AuthContainer>
      <StyledActivities>
        <Grid container spacing={2}>
          <Grid item xs={12} md={9}>
            <Paper sx={{ p: 2 }}>
              <Calendar
                campaigns={campaigns || []}
                selectedEvent={selectedCampaign}
                onSelectEvent={handleSelectCampaignForEdit}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2 }}>
              <CampaignForm
                key={selectedCampaign?.id}
                campaign={selectedCampaign}
                recipientList={recipientList}
                onCancel={handleResetCampaignForEdit}
                onSubmit={handleResetCampaignForEdit}
              />
            </Paper>
            <Paper sx={{ p: 2, mt: 2 }}>
              <UpcomingCampaigns
                campaigns={upcomingCampaigns || []}
                onEditClick={handleSelectCampaignForEdit}
                onCancelClick={handleDeleteCampaign}
              />
            </Paper>
          </Grid>
        </Grid>
      </StyledActivities>
    </AuthContainer>
  );
};

export default MessageScheduler;
